import { createClient } from '@supabase/supabase-js';
import { ENV } from '@shared/config/env';

const supabaseUrl = ENV.SUPABASE.URL;
const supabaseAnonKey = ENV.SUPABASE.ANON_KEY;

// Check if we have real Supabase credentials (not placeholder values)
const hasRealCredentials = supabaseUrl && 
  supabaseAnonKey && 
  !supabaseUrl.includes('placeholder') && 
  !supabaseAnonKey.includes('placeholder');

export const isSupabaseConfigured = Boolean(hasRealCredentials);

export const missingSupabaseConfigMessage =
  'Running in MOCK DATA mode (Supabase not connected). This is normal for development. All data is simulated.';

const createMissingSupabaseClient = () => {
  const noop = () => {};

  // Suppress unhandled rejection noise — callers can still .catch() if they want
  const createRejectedPromise = () => {
    const p = Promise.reject(new Error(missingSupabaseConfigMessage));
    p.catch(noop); // prevent UnhandledPromiseRejection in console
    return p;
  };

  const createQueryProxy = () => {
    const rejectedPromise = createRejectedPromise();

    return new Proxy(
      {},
      {
        get(_target, property) {
          if (property === 'then') {
            return rejectedPromise.then.bind(rejectedPromise);
          }

          if (property === 'catch') {
            return rejectedPromise.catch.bind(rejectedPromise);
          }

          if (property === 'finally') {
            return rejectedPromise.finally.bind(rejectedPromise);
          }

          return () => createQueryProxy();
        },
      },
    );
  };

  // Auth mock — returns safe no-op stubs so AuthProvider loads without throwing
  const authMock = {
    getSession: () => Promise.resolve({ data: { session: null }, error: null }),
    getUser: () => Promise.resolve({ data: { user: null }, error: null }),
    signInWithPassword: createRejectedPromise,
    signOut: createRejectedPromise,
    signUp: createRejectedPromise,
    resetPasswordForEmail: createRejectedPromise,
    updateUser: createRejectedPromise,
    onAuthStateChange: (_event, _callback) => ({
      data: { subscription: { unsubscribe: noop } },
    }),
  };

  return new Proxy(
    {},
    {
      get(_target, property) {
        if (property === 'from' || property === 'rpc' || property === 'channel') {
          return () => createQueryProxy();
        }

        if (property === 'auth') {
          return authMock;
        }

        if (property === 'storage' || property === 'functions') {
          return new Proxy({}, { get: () => () => createQueryProxy() });
        }

        if (property === 'then' || property === 'catch' || property === 'finally') {
          const rejectedPromise = createRejectedPromise();
          return rejectedPromise[property].bind(rejectedPromise);
        }

        return () => createQueryProxy();
      },
    },
  );
};

// Capture URL hash BEFORE Supabase processes it
// This must happen at module load time
// Guard against SSR/test environments where window might not exist
const initialUrlHash = typeof window !== 'undefined' ? window.location.hash : '';
const hashParams = new URLSearchParams(initialUrlHash.substring(1));
export const recoveryType = hashParams.get('type'); // 'recovery', 'invite', 'signup', etc.

// Mutable flag that can be cleared after use
let _isRecoveryUrl = recoveryType === 'recovery' || recoveryType === 'invite' || recoveryType === 'signup';
export const isRecoveryUrl = () => _isRecoveryUrl;
export const clearRecoveryFlag = () => { _isRecoveryUrl = false; };

// Also check URL query params for error messages (expired links show errors in query string)
const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
export const authErrorCode = urlParams.get('error_code');
export const authErrorDescription = urlParams.get('error_description');
export const hasAuthError = !!authErrorCode;

export const supabase = isSupabaseConfigured
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        // Let Supabase handle URL detection, but we'll check our captured hash
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
      global: {
        fetch: async (...args) => {
          const maxRetries = 3;
          const retryDelay = 1000; // 1 second
          
          for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
              const response = await fetch(...args);
              
              // If successful or client error (4xx), return immediately
              if (response.ok || (response.status >= 400 && response.status < 500)) {
                return response;
              }
              
              // Server error (5xx) - retry if we have attempts left
              if (response.status >= 500 && attempt < maxRetries) {
                console.warn(`Supabase request failed with ${response.status}, retrying in ${retryDelay}ms (attempt ${attempt}/${maxRetries})`);
                await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
                continue;
              }
              
              return response;
            } catch (error) {
              // Network error - retry if we have attempts left
              if (attempt < maxRetries) {
                console.warn(`Supabase network error, retrying in ${retryDelay}ms (attempt ${attempt}/${maxRetries}):`, error.message);
                await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
                continue;
              }
              
              // Final attempt failed
              throw error;
            }
          }
          
          throw new Error('Max retries exceeded');
        }
      }
    })
  : createMissingSupabaseClient();

if (!isSupabaseConfigured) {
  // eslint-disable-next-line no-console
  console.warn(missingSupabaseConfigMessage);
}

